import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import type { useContentReviewerNudgeOnCriteria_content$key } from './__generated__/useContentReviewerNudgeOnCriteria_content.graphql.ts';
import { useContentReviewerNudgeAction } from './useContentReviewerNudgeAction.tsx';
import { useContentReviewerNudgeOnCriteria } from './useContentReviewerNudgeOnCriteria.tsx';
import { useContentReviewerNudgeOnPublishSignal } from './useContentReviewerNudgeOnPublishSignal.ts';

interface UseContentReviewerNudgeProps {
	contentFragmentRef?: useContentReviewerNudgeOnCriteria_content$key | null;
	/** The current content id, used to detect the OnPublish event trigger for this page. */
	contentId: string;
}

/**
 * Entrypoint for the Content Reviewer nudge. Returns the shared action when either trigger fires:
 *  - the in-editor criteria match (content-state transition into "Ready for review" + word count —
 *    see `useContentReviewerNudgeOnCriteria`), or
 *  - the OnPublish signal fires for this content (the page was just published — see
 *    `useContentReviewerNudgeOnPublishSignal`).
 *
 * Both triggers resolve to the same action via `useContentReviewerNudgeAction`, so they share one
 * action id, experiment gate, and dismissal bookkeeping.
 */
export const useContentReviewerNudge = ({
	contentFragmentRef,
	contentId,
}: UseContentReviewerNudgeProps): ConversationAction | null => {
	const matchesCriteria = useContentReviewerNudgeOnCriteria({ contentFragmentRef });
	const hasOnPublishSignal = useContentReviewerNudgeOnPublishSignal(contentId);

	return useContentReviewerNudgeAction(matchesCriteria || hasOnPublishSignal);
};
